/**
 * Reading the current screen's Filter_Context from inside React
 * (`complan-governance` Requirement 6 criterion 3).
 *
 * The codec in `filter-context.ts` is pure and testable without a router; this is the
 * thin hook that tells it where the user currently is. Split that way for the reason
 * every other codec in this folder is: the interesting logic stays unit-testable
 * without mounting anything, and the part that needs a router is small enough to read
 * at a glance.
 */
import { useLocation, useParams } from 'react-router-dom';
import {
  readFilterContext,
  FILTER_SCREENS,
  type FilterContext,
  type FilterScreen,
} from './filter-context';

/**
 * Which participating screen a pathname belongs to, or `undefined` for a screen
 * outside the Filter_Context set.
 *
 * Matched longest-prefix-first so `/governance` cannot be mistaken for a prefix of
 * something else later, and so a nested route (the Detail_Panel under
 * `/calendar/:year/initiative/:id`) still resolves to its parent screen - a panel
 * open over the calendar is still the calendar for Filter_Context purposes.
 */
export function screenForPathname(pathname: string): FilterScreen | undefined {
  const candidates: readonly (readonly [string, FilterScreen])[] = [
    ['/calendar', 'calendar'],
    ['/initiatives', 'initiative-list'],
    ['/governance', 'governance-dashboard'],
    ['/dashboard', 'management-dashboard'],
  ];
  for (const [prefix, screen] of candidates) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return screen;
    }
  }
  return undefined;
}

/**
 * The Filter_Context of the screen the user is on, or `undefined` when the current
 * screen is not one that carries it.
 *
 * `undefined` rather than an empty context, so a caller can tell "this screen carries
 * nothing" from "this screen is not part of the set" - the sidebar needs that
 * distinction to leave a plain link plain rather than appending an empty query.
 */
export function useFilterContext(): FilterContext | undefined {
  const location = useLocation();
  const params = useParams();

  const screen = screenForPathname(location.pathname);
  if (screen === undefined) {
    return undefined;
  }

  const search = new URLSearchParams(location.search);
  const pathYear =
    FILTER_SCREENS[screen].yearIn === 'path' ? parsePathYear(params['year']) : undefined;

  return readFilterContext({ screen, search, pathYear });
}

/** The `:year` route segment as a number, or `undefined` when absent or malformed. */
function parsePathYear(raw: string | undefined): number | undefined {
  if (raw === undefined) {
    return undefined;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) ? parsed : undefined;
}
